import React, { useState, useEffect, ChangeEvent } from 'react';
import {
  Box,
  Alert,
  Typography,
  Button,
  CircularProgress,
  Modal,
  IconButton,
  MenuItem,
  FormControl,
  InputLabel,
  Grid,
  Container,
  CardContent,
  Fade,
  InputAdornment,
  Paper,
  Backdrop,
} from '@mui/material';
import { SelectChangeEvent } from '@mui/material/Select';
import CloseIcon from '@mui/icons-material/Close';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import PersonIcon from '@mui/icons-material/Person';
import PhotoCameraIcon from '@mui/icons-material/PhotoCamera';
import BadgeIcon from '@mui/icons-material/Badge';
import HomeIcon from '@mui/icons-material/Home';
import PhoneIcon from '@mui/icons-material/Phone';
import WorkIcon from '@mui/icons-material/Work';
import FavoriteIcon from '@mui/icons-material/Favorite';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import EditIcon from '@mui/icons-material/Edit';
import { useLocation, useNavigate } from 'react-router-dom';

import modifieVoyageur from '../modifieVoyageurService';
import { recupererUsageVoyageur } from '../Services';
import { Voyageurs } from '../models/Voyageurs';
import DocumentPicker from './commun/DocumentPicker';
import {
  SectionTitle,
  StyledCard,
  StyledSelect,
  StyledTextField,
} from './commun/formStyles';

// ----------------------------------------------------------------
// Interface pour récupérer le voyageur depuis l'état de la route
// ----------------------------------------------------------------

interface LocationState {
  voyageur: Voyageurs;
}

// ----------------------------------------------------------------
// Composant principal VoyageurUpdate
// ----------------------------------------------------------------

const VoyageurUpdate: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const state = location.state as LocationState | undefined;
  const initialVoyageur = state?.voyageur || null;

  const [voyageur, setVoyageur] = useState<Voyageurs | null>(initialVoyageur);

  // États de validation
  const [nomError, setNomError] = useState(false);
  const [prenomError, setPrenomError] = useState(false);
  const [telephoneError, setTelephoneError] = useState(false);
  const [professionError, setProfessionError] = useState(false);
  const [etatCivilError, setEtatCivilError] = useState(false);

  // États pour la gestion de l'adresse
  const [adresse, setAdresse] = useState(voyageur?.adresse || '');
  const [codePostal, setCodePostal] = useState(voyageur?.cp || '');
  const [ville, setVille] = useState(voyageur?.ville || '');
  const [adresseIdentique, setAdresseIdentique] = useState(
    voyageur?.adresse ? 'non' : 'oui'
  );

  // États pour les photos/documents
  const [selectedPhotoUri, setSelectedPhotoUri] = useState<string | null>(
    voyageur?.url || null
  );
  const defaultPasseportUri = voyageur?.url
    ? voyageur.url.replace('photo.jpg', 'passeport.jpg')
    : '';
  const [selectedPasseportUri, setSelectedPasseportUri] = useState<string | null>(
    defaultPasseportUri || null
  );

  // État civil
  const [etatCivil, setEtatCivil] = useState(voyageur?.etat_civil || '');

  // État de chargement
  const [isLoading, setIsLoading] = useState(false);
  const [isFormValid, setIsFormValid] = useState(false);

  // Verrou : si le voyageur a déjà servi à un dossier (hors annulé/refusé), profil non modifiable
  const [isLocked, setIsLocked] = useState<boolean>(
    (initialVoyageur?.nb_dossiers_actifs ?? 0) > 0
  );

  // États pour le Modal d'aperçu d'image
  const [modalVisible, setModalVisible] = useState(false);
  const [previewImageUri, setPreviewImageUri] = useState<string | null>(null);

  // Redirection si aucun voyageur n'est disponible
  useEffect(() => {
    if (!voyageur) {
      navigate(-1);
    }
  }, [voyageur, navigate]);

  // Vérifie côté serveur si le profil doit être verrouillé (dossier existant hors annulé/refusé)
  useEffect(() => {
    if (voyageur?.id) {
      recupererUsageVoyageur(voyageur.id)
        .then((usage) => setIsLocked(usage.actifs > 0))
        .catch(() => {});
    }
  }, [voyageur?.id]);

  // Validation du formulaire à chaque changement
  useEffect(() => {
    const isValid = validateForm();
    setIsFormValid(isValid);
  }, [
    voyageur?.nom,
    voyageur?.prenom,
    voyageur?.telephone,
    voyageur?.profession,
    etatCivil,
    adresseIdentique,
    adresse,
    codePostal,
    ville,
  ]);

  // Validation du formulaire
  const validateForm = (): boolean => {
    let isValid = true;

    if (!voyageur?.nom.trim()) {
      setNomError(true);
      isValid = false;
    } else {
      setNomError(false);
    }

    if (!voyageur?.prenom.trim()) {
      setPrenomError(true);
      isValid = false;
    } else {
      setPrenomError(false);
    }

    if (!etatCivil.trim()) {
      setEtatCivilError(true);
      isValid = false;
    } else {
      setEtatCivilError(false);
    }

    const phoneRegex = /^\d+$/;
    if (!voyageur?.telephone.trim() || !phoneRegex.test(voyageur.telephone.trim())) {
      setTelephoneError(true);
      isValid = false;
    } else {
      setTelephoneError(false);
    }

    if (!voyageur?.profession.trim()) {
      setProfessionError(true);
      isValid = false;
    } else {
      setProfessionError(false);
    }

    if (adresseIdentique === 'non') {
      if (!adresse.trim() || !codePostal.trim() || !ville.trim()) {
        isValid = false;
      }
    }
    return isValid;
  };

  // Soumission du formulaire
  const handleSubmit = async () => {
    if (isLocked) return;
    if (isFormValid && voyageur) {
      setIsLoading(true);
      try {
        const newPhotoUri =
          selectedPhotoUri !== voyageur.url ? selectedPhotoUri : null;
        const newPasseportUri =
          selectedPasseportUri !== defaultPasseportUri ? selectedPasseportUri : null;

        await modifieVoyageur(
          voyageur.id,
          voyageur.nom,
          voyageur.prenom,
          newPhotoUri,
          newPasseportUri,
          adresseIdentique === 'non' ? adresse : '',
          adresseIdentique === 'non' ? codePostal : '',
          adresseIdentique === 'non' ? ville : '',
          voyageur.telephone,
          voyageur.profession,
          etatCivil
        );
        setIsLoading(false);
        navigate(-1);
      } catch (error) {
        console.error('Erreur lors de la mise à jour :', error);
        setIsLoading(false);
      }
    }
  };

  // Gestion de l'ouverture/fermeture du Modal
  const handleOpenModal = (uri: string) => {
    setPreviewImageUri(uri);
    setModalVisible(true);
  };

  const handleCloseModal = () => {
    setModalVisible(false);
    setPreviewImageUri(null);
  };

  // Mise à jour des champs du voyageur
  const handleChangeVoyageurField = (field: keyof Voyageurs, value: string) => {
    setVoyageur((prev) => (prev ? { ...prev, [field]: value } : prev));
  };

  // Filtrage du téléphone
  const handleTelephoneChange = (e: ChangeEvent<HTMLInputElement>) => {
    const filteredText = e.target.value.replace(/[^0-9]/g, '');
    handleChangeVoyageurField('telephone', filteredText);
  };

  return (
    <>
      {voyageur ? (
        <Box sx={{ backgroundImage: 'url(https://beta.visamonde.com/assets/mobile/fond.jpg)', backgroundSize: 'cover', backgroundPosition: 'center', backgroundRepeat: 'no-repeat', minHeight: '100vh', py: { xs: 2, sm: 3 } }}>
          <Container maxWidth="lg">
            {/* Header */}
            <Paper
              elevation={0}
              sx={{
                p: { xs: 2, sm: 3 },
                mb: { xs: 3, sm: 4 },
                borderRadius: 3,
                background: 'linear-gradient(135deg, #2e7d32 0%, #1b5e20 100%)',
                color: 'white',
              }}
            >
              <Box display="flex" alignItems="center">
                <IconButton
                  onClick={() => navigate(-1)}
                  sx={{
                    color: 'white',
                    bgcolor: 'rgba(255, 255, 255, 0.2)',
                    '&:hover': { bgcolor: 'rgba(255, 255, 255, 0.3)' },
                  }}
                >
                  <ArrowBackIcon />
                </IconButton>
                <Box ml={2}>
                  <Typography variant="h4" fontWeight="bold" sx={{ fontSize: { xs: '1.4rem', sm: '2rem' } }}>
                    Mettre à jour le voyageur
                  </Typography>
                  <Typography variant="subtitle1" sx={{ opacity: 0.9, mt: 0.5, fontSize: { xs: '0.85rem', sm: '1rem' } }}>
                    Modifiez les informations de {voyageur.prenom} {voyageur.nom}
                  </Typography>
                </Box>
              </Box>
            </Paper>

            <Backdrop
              sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 1 }}
              open={isLoading}
            >
              <CircularProgress color="inherit" size={60} />
            </Backdrop>

            {isLocked && (
              <Alert severity="info" sx={{ mb: 2 }}>
                Ce voyageur est lié à un dossier&nbsp;: son profil ne peut plus être
                modifié afin de garantir la cohérence avec le passeport utilisé. Pour
                toute correction, contactez VisaMonde.
              </Alert>
            )}

            <fieldset
              disabled={isLocked}
              style={{ border: 'none', padding: 0, margin: 0, minInlineSize: 'auto' }}
            >
            <Grid container spacing={3}>
              {/* Informations personnelles */}
              <Grid item xs={12} md={6}>
                <StyledCard>
                  <CardContent sx={{ p: 4 }}>
                    <SectionTitle variant="h6">
                      <PersonIcon color="primary" />
                      Informations personnelles
                    </SectionTitle>
                    <Grid container spacing={2}>
                      <Grid item xs={12} sm={6}>
                        <StyledTextField
                          fullWidth
                          label="Nom"
                          value={voyageur.nom}
                          error={nomError}
                          helperText={nomError ? 'Veuillez remplir ce champ' : ''}
                          onChange={(e) => handleChangeVoyageurField('nom', e.target.value)}
                          variant="outlined"
                          InputProps={{
                            startAdornment: (
                              <InputAdornment position="start">
                                <PersonIcon fontSize="small" />
                              </InputAdornment>
                            ),
                          }}
                        />
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <StyledTextField
                          fullWidth
                          label="Prénom"
                          value={voyageur.prenom}
                          error={prenomError}
                          helperText={prenomError ? 'Veuillez remplir ce champ' : ''}
                          onChange={(e) => handleChangeVoyageurField('prenom', e.target.value)}
                          variant="outlined"
                          InputProps={{
                            startAdornment: (
                              <InputAdornment position="start">
                                <PersonIcon fontSize="small" />
                              </InputAdornment>
                            ),
                          }}
                        />
                      </Grid>
                      <Grid item xs={12}>
                        <FormControl fullWidth error={etatCivilError}>
                          <InputLabel id="etat-civil-label">État Civil</InputLabel>
                          <StyledSelect
                            labelId="etat-civil-label"
                            value={etatCivil}
                            label="État Civil"
                            onChange={(e) => setEtatCivil(e.target.value as string)}
                            startAdornment={
                              <InputAdornment position="start">
                                <FavoriteIcon fontSize="small" />
                              </InputAdornment>
                            }
                          >
                            <MenuItem value="célibataire">Célibataire</MenuItem>
                            <MenuItem value="marié">Marié(e)</MenuItem>
                            <MenuItem value="divorcé">Divorcé(e)</MenuItem>
                            <MenuItem value="veuf">Veuf/Veuve</MenuItem>
                          </StyledSelect>
                          {etatCivilError && (
                            <Typography variant="caption" color="error" sx={{ mt: 1 }}>
                              Veuillez sélectionner un état civil.
                            </Typography>
                          )}
                        </FormControl>
                      </Grid>
                    </Grid>
                  </CardContent>
                </StyledCard>

                {/* Contact et profession */}
                <StyledCard>
                  <CardContent sx={{ p: 4 }}>
                    <SectionTitle variant="h6">
                      <PhoneIcon color="primary" />
                      Contact et profession
                    </SectionTitle>
                    <Grid container spacing={2}>
                      <Grid item xs={12}>
                        <StyledTextField
                          fullWidth
                          label="Téléphone"
                          value={voyageur.telephone}
                          error={telephoneError}
                          helperText={
                            telephoneError
                              ? 'Veuillez entrer un numéro valide (chiffres uniquement).'
                              : ''
                          }
                          onChange={handleTelephoneChange}
                          variant="outlined"
                          inputProps={{ inputMode: 'numeric' }}
                          InputProps={{
                            startAdornment: (
                              <InputAdornment position="start">
                                <PhoneIcon fontSize="small" />
                              </InputAdornment>
                            ),
                          }}
                        />
                      </Grid>
                      <Grid item xs={12}>
                        <StyledTextField
                          fullWidth
                          label="Profession"
                          value={voyageur.profession}
                          error={professionError}
                          helperText={professionError ? 'Veuillez remplir ce champ' : ''}
                          onChange={(e) => handleChangeVoyageurField('profession', e.target.value)}
                          variant="outlined"
                          InputProps={{
                            startAdornment: (
                              <InputAdornment position="start">
                                <WorkIcon fontSize="small" />
                              </InputAdornment>
                            ),
                          }}
                        />
                      </Grid>
                    </Grid>
                  </CardContent>
                </StyledCard>

                {/* Adresse */}
                <StyledCard>
                  <CardContent sx={{ p: 4 }}>
                    <SectionTitle variant="h6">
                      <HomeIcon color="primary" />
                      Adresse
                    </SectionTitle>
                    <Grid container spacing={2}>
                      <Grid item xs={12}>
                        <FormControl fullWidth>
                          <InputLabel id="adresse-identique-label">
                            Adresse identique à celle du passeport ?
                          </InputLabel>
                          <StyledSelect
                            labelId="adresse-identique-label"
                            value={adresseIdentique}
                            label="Adresse identique à celle du passeport ?"
                            onChange={(e) => {
                              setAdresseIdentique(e.target.value as string);
                              if (e.target.value === 'oui') {
                                setAdresse('');
                                setCodePostal('');
                                setVille('');
                              }
                            }}
                          >
                            <MenuItem value="oui">Oui</MenuItem>
                            <MenuItem value="non">Non</MenuItem>
                          </StyledSelect>
                        </FormControl>
                      </Grid>

                      {adresseIdentique === 'non' && (
                        <Fade in={true}>
                          <Grid container spacing={2} sx={{ mt: 0 }}>
                            <Grid item xs={12}>
                              <StyledTextField
                                fullWidth
                                label="Adresse"
                                value={adresse}
                                onChange={(e) => setAdresse(e.target.value)}
                                variant="outlined"
                                InputProps={{
                                  startAdornment: (
                                    <InputAdornment position="start">
                                      <HomeIcon fontSize="small" />
                                    </InputAdornment>
                                  ),
                                }}
                              />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                              <StyledTextField
                                fullWidth
                                label="Code Postal"
                                value={codePostal}
                                onChange={(e) => setCodePostal(e.target.value)}
                                variant="outlined"
                                type="tel"  // Ajouter cette ligne
                                inputProps={{ 
                                  inputMode: 'numeric',
                                  pattern: '[0-9]*'  // Ajouter pour iOS
                                }}
                              />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                              <StyledTextField
                                fullWidth
                                label="Ville"
                                value={ville}
                                onChange={(e) => setVille(e.target.value)}
                                variant="outlined"
                              />
                            </Grid>
                          </Grid>
                        </Fade>
                      )}
                    </Grid>
                  </CardContent>
                </StyledCard>
              </Grid>

              {/* Documents */}
              <Grid item xs={12} md={6}>
                <StyledCard>
                  <CardContent sx={{ p: 4 }}>
                    <SectionTitle variant="h6">
                      <PhotoCameraIcon color="primary" />
                      Documents d'identité
                    </SectionTitle>
                    <Grid container spacing={3}>
                      <Grid item xs={12}>
                        <DocumentPicker
                          documentType="Photo d'identité"
                          onDocumentSelected={setSelectedPhotoUri}
                          modeOptions={['photo', 'selectPhoto']}
                          icon={<PhotoCameraIcon />}
                          currentUri={selectedPhotoUri}
                        />
                        <Box sx={{ mt: 2, p: 2, bgcolor: 'info.lighter', borderRadius: 2, border: '1px solid', borderColor: 'info.main' }}>
                          <Typography variant="caption" color="info.main" sx={{ display: 'block', mb: 1, fontWeight: 500 }}>
                            Exemple de photo d'identité :
                          </Typography>
                          <Box
                            component="img"
                            src="/photoexemple.jpg"
                            alt="Exemple de photo d'identité"
                            sx={{
                              width: '100%',
                              maxWidth: 200,
                              height: 'auto',
                              borderRadius: 1,
                              boxShadow: 2,
                              display: 'block',
                              mx: 'auto'
                            }}
                          />
                          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1, textAlign: 'center' }}>
                            Photo récente sur fond clair, visage visible
                          </Typography>
                        </Box>
                      </Grid>
                      <Grid item xs={12}>
                        <DocumentPicker
                          documentType="Passeport"
                          onDocumentSelected={setSelectedPasseportUri}
                          modeOptions={['photo', 'selectPhoto', 'selectPdf']}
                          icon={<BadgeIcon />}
                          currentUri={selectedPasseportUri}
                        />
                        <Box sx={{ mt: 2, p: 2, bgcolor: 'info.lighter', borderRadius: 2, border: '1px solid', borderColor: 'info.main' }}>
                          <Typography variant="caption" color="info.main" sx={{ display: 'block', mb: 1, fontWeight: 500 }}>
                            Exemple de passeport :
                          </Typography>
                          <Box
                            component="img"
                            src="/paxexemple.jpg"
                            alt="Exemple de passeport"
                            sx={{
                              width: '100%',
                              maxWidth: 300,
                              height: 'auto',
                              borderRadius: 1,
                              boxShadow: 2,
                              display: 'block',
                              mx: 'auto'
                            }}
                          />
                          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1, textAlign: 'center' }}>
                            Page d'identité du passeport avec photo et informations lisibles
                          </Typography>
                        </Box>
                      </Grid>
                    </Grid>
                  </CardContent>
                </StyledCard>
              </Grid>

              {/* Bouton de soumission */}
              <Grid item xs={12}>
                <Box sx={{ mt: 2, mb: 3, display: 'flex', justifyContent: 'center' }}>
                  <Button
                    variant="contained"
                    size="large"
                    disabled={!isFormValid || isLocked}
                    onClick={handleSubmit}
                    startIcon={<EditIcon />}
                    sx={{
                      borderRadius: 3,
                      py: 1.5,
                      px: { xs: 4, sm: 6 },
                      width: { xs: '100%', sm: 'auto' },
                      fontSize: { xs: '1rem', sm: '1.1rem' },
                      textTransform: 'none',
                      fontWeight: 600,
                      background: isFormValid
                        ? 'linear-gradient(135deg, #2e7d32 0%, #1b5e20 100%)'
                        : undefined,
                      boxShadow: isFormValid ? '0 4px 14px rgba(46,125,50,0.3)' : undefined,
                      '&:hover': {
                        background: isFormValid
                          ? 'linear-gradient(135deg, #1b5e20 0%, #145218 100%)'
                          : undefined,
                        boxShadow: isFormValid ? '0 6px 20px rgba(46,125,50,0.4)' : undefined,
                      },
                    }}
                  >
                    Mettre à jour le voyageur
                  </Button>
                </Box>
              </Grid>
            </Grid>
            </fieldset>
          </Container>
        </Box>
      ) : null}

      {/* Modal d'aperçu d'image */}
      <Modal open={modalVisible} onClose={handleCloseModal}>
        <Box
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            bgcolor: 'background.paper',
            borderRadius: 2,
            boxShadow: 24,
            p: 2,
            maxWidth: '90vw',
            maxHeight: '90vh',
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          <IconButton
            onClick={handleCloseModal}
            sx={{
              position: 'absolute',
              right: 8,
              top: 8,
              bgcolor: 'background.paper',
              boxShadow: 1,
              '&:hover': {
                bgcolor: 'background.default',
              },
            }}
          >
            <CloseIcon />
          </IconButton>
          {previewImageUri && (
            <Box
              component="img"
              src={previewImageUri}
              alt="aperçu"
              sx={{
                maxWidth: '100%',
                maxHeight: '80vh',
                objectFit: 'contain',
                mt: 4,
              }}
            />
          )}
        </Box>
      </Modal>
    </>
  );
};

export default VoyageurUpdate;